// Self-contained V2 API layer — mirrors TrainingAPI's axios/base-URL pattern without touching it.
// Auth headers are inherited from the global axios defaults set at login.
// Verified against api/urls.py + StatusTrackerV2Views.py on the tracker_v2_read_endpoint chain.
import axios from "axios";

const BASE =
    import.meta.env.MODE === "production"
        ? "https://api.gpsdataviz.com/api"
        : "http://127.0.0.1/api";

const ROOT = `${BASE}/status-tracker/v2`;

// POST /coach-flag-tags  { player_id, flag_id, date, notes } — tag one player on one date.
// Not under /v2: the coach tag endpoints are shared with V1. Returns counts only
// ({created, updated, rows_created, rows_updated}), so the caller builds the cell entry itself.
export const tagCoachFlag = (body) => axios.post(`${BASE}/coach-flag-tags`, body).then((r) => r.data);

// DELETE /coach-flag-tags?player_id&flag_id&date — untag one player on one date.
// Returns {removed, rows_deleted, rows_updated}; removing a tag that is not there is harmless.
export const untagCoachFlag = (params) =>
    axios.delete(`${BASE}/coach-flag-tags`, { params }).then((r) => r.data);

// POST /coach-flag-tags/bulk  { flag_id, targets: [{ player_id, date, notes }] } — one entry per
// named cell, each with its own note. Same response shape as the single call, one tag per cell.
export const tagCoachFlagsBulk = (body) =>
    axios.post(`${BASE}/coach-flag-tags/bulk`, body).then((r) => r.data);

// DELETE /coach-flag-tags/bulk  { flag_id, targets: [{ player_id, date }] } — exactly these
// cells. The {dates, player_ids} form deletes every combination of the two, which would also
// clear cells that merely share a player or a date with the ones being removed.
export const untagCoachFlagsBulk = (body) =>
    axios.delete(`${BASE}/coach-flag-tags/bulk`, { data: body }).then((r) => r.data);

// GET /v2/month-view?start_date&end_date — whole range in one call. Returns { '<pid>_<date>': cell }.
// 403 when the team is not V2-enabled.
export const getMonthView = (startDate, endDate) =>
    axios
        .get(`${ROOT}/month-view`, { params: { start_date: startDate, end_date: endDate } })
        .then((r) => r.data);

// GET /v2/status-types?start_date&end_date — [{ id, name, color }], ordered by usage over the range.
export const getStatusTypes = (startDate, endDate) =>
    axios
        .get(`${ROOT}/status-types`, { params: { start_date: startDate, end_date: endDate } })
        .then((r) => r.data);

// POST /v2/status-types  { name, color } → created type; 409 if the name is taken.
export const createStatusType = (body) => axios.post(`${ROOT}/status-types`, body).then((r) => r.data);

// PATCH /v2/status-types/{id}  { name?, color? } → updated type; 404 not found, 409 name clash.
export const updateStatusType = (id, body) =>
    axios.patch(`${ROOT}/status-types/${id}`, body).then((r) => r.data);

// DELETE /v2/status-types/{id}?player_id → 204; 404 not found, 409 in use (mapped to other players).
// player_id lets the server allow deletion when the type is used only by the current player (V1 rule).
export const deleteStatusType = (id, playerId) =>
    axios.delete(`${ROOT}/status-types/${id}`, { params: { player_id: playerId } }).then((r) => r.data);

// POST /v2/player-status  { player_id, date, status_id } → the assigned status dict.
export const assignPlayerStatus = ({ player_id, date, status_id }) =>
    axios.post(`${ROOT}/player-status`, { player_id, date, status_id }).then((r) => r.data);

// DELETE /v2/player-status?player_id&date&status_id → the reverted default / "No Data" status dict.
export const clearPlayerStatus = ({ player_id, date, status_id }) =>
    axios.delete(`${ROOT}/player-status`, { params: { player_id, date, status_id } }).then((r) => r.data);
